import i18n from "../i18n";

// Global Danish number formatting solution
class DanishNumberFormatter {
  constructor() {
    this.isInitialized = false;
    this.originalToFixed = Number.prototype.toFixed;
    this.originalToLocaleString = Number.prototype.toLocaleString;
    this.originalParseFloat = window.parseFloat;
    this.originalNumber = window.Number;
    this.originalIsNaN = window.isNaN;
    this.originalIsFinite = window.isFinite;
  }

  // Helper function to get current language
  getCurrentLanguage() {
    try {
      if (i18n && i18n.language) {
        return i18n.language;
      } else if (typeof localStorage !== "undefined") {
        return localStorage.getItem("i18nextLng") || "da";
      }
    } catch (e) {
      // Fallback to Danish if there's any error
    }
    return "da"; // Default to Danish
  }

  // Helper function to check if current language should use comma formatting
  shouldUseCommaFormatting() {
    const currentLanguage = this.getCurrentLanguage();
    // Use comma formatting for all languages except English and Indonesian
    return currentLanguage !== "en" && currentLanguage !== "id";
  }

  initialize() {
    if (this.isInitialized) return;

    console.log(
      "🌍 Initializing global number formatting (comma for all languages except English and Indonesian)..."
    );

    // Store original functions before any overrides
    this.originalToFixed = Number.prototype.toFixed;
    this.originalToLocaleString = Number.prototype.toLocaleString;
    this.originalParseFloat = window.parseFloat;
    this.originalNumber = window.Number;
    this.originalIsNaN = window.isNaN;
    this.originalIsFinite = window.isFinite;

    // Override Number.prototype.toFixed for comma formatting languages
    Number.prototype.toFixed = function (decimals = 0) {
      const result = danishNumberFormatter.originalToFixed.call(this, decimals);

      if (danishNumberFormatter.shouldUseCommaFormatting()) {
        return result.replace(".", ",");
      }
      return result;
    };

    // Override Number.prototype.toLocaleString for comma formatting languages
    Number.prototype.toLocaleString = function (locales, options) {
      const currentLanguage = danishNumberFormatter.getCurrentLanguage();

      if (danishNumberFormatter.shouldUseCommaFormatting()) {
        // Use appropriate locale for comma formatting
        const localeMap = {
          da: "da-DK",
          de: "de-DE",
          fr: "fr-FR",
          nl: "nl-NL",
        };
        const locale = localeMap[currentLanguage] || "da-DK";
        return danishNumberFormatter.originalToLocaleString.call(
          this,
          locale,
          options
        );
      }
      return danishNumberFormatter.originalToLocaleString.call(
        this,
        locales,
        options
      );
    };

    // Override parseFloat to handle comma decimal separator
    window.parseFloat = function (str) {
      if (typeof str === "string") {
        if (danishNumberFormatter.shouldUseCommaFormatting()) {
          // Convert comma to dot for parsing
          str = str.replace(",", ".");
        }
      }
      return danishNumberFormatter.originalParseFloat(str);
    };

    // Override Number constructor to handle comma decimal separator
    const originalNumber = window.Number;
    window.Number = function (value) {
      if (typeof value === "string") {
        if (danishNumberFormatter.shouldUseCommaFormatting()) {
          // Convert comma to dot for parsing
          value = value.replace(",", ".");
        }
      }
      return originalNumber(value);
    };

    // Copy static methods from original Number
    Object.setPrototypeOf(window.Number, originalNumber);
    Object.defineProperty(window.Number, "prototype", {
      value: originalNumber.prototype,
      writable: false,
    });

    // Override isNaN to handle comma decimal separator
    window.isNaN = function (value) {
      if (typeof value === "string") {
        if (danishNumberFormatter.shouldUseCommaFormatting()) {
          // Convert comma to dot for parsing
          value = value.replace(",", ".");
        }
      }
      return danishNumberFormatter.originalIsNaN(value);
    };

    // Override isFinite to handle comma decimal separator
    window.isFinite = function (value) {
      if (typeof value === "string") {
        if (danishNumberFormatter.shouldUseCommaFormatting()) {
          // Convert comma to dot for parsing
          value = value.replace(",", ".");
        }
      }
      return danishNumberFormatter.originalIsFinite(value);
    };

    // Override parseInt to handle comma decimal separator (though it shouldn't be needed for integers)
    const originalParseInt = window.parseInt;
    window.parseInt = function (string, radix) {
      if (typeof string === "string") {
        if (danishNumberFormatter.shouldUseCommaFormatting()) {
          // Only convert comma if it's not part of a larger number format
          // This is more conservative to avoid breaking integer parsing
          if (/^\d+,\d*$/.test(string)) {
            string = string.replace(",", ".");
          }
        }
      }
      return originalParseInt(string, radix);
    };

    // Override JSON.stringify to format numbers for display (but not for API calls)
    const originalJSONStringify = JSON.stringify;
    JSON.stringify = function (value, replacer, space) {
      // Only format for display if we're not in an API context
      // We can detect this by checking if we're in a fetch/xhr context
      const isApiContext = window._isApiCall || false;

      if (
        !isApiContext &&
        danishNumberFormatter.shouldUseCommaFormatting() &&
        typeof value === "object" &&
        value !== null
      ) {
        // Deep clone and format numbers for display
        const formattedValue =
          danishNumberFormatter.formatObjectForDisplay(value);
        return originalJSONStringify(formattedValue, replacer, space);
      }

      return originalJSONStringify(value, replacer, space);
    };

    this.isInitialized = true;
    console.log("✅ Global number formatting initialized successfully!");

    // Test the overrides immediately
    const testNumber = 1234.56;
    const testResult = testNumber.toFixed(2);
    const currentLang = this.getCurrentLanguage();
    const shouldUseComma = this.shouldUseCommaFormatting();
    console.log(
      `🧪 Test: ${testNumber}.toFixed(2) = "${testResult}" (Language: ${currentLang}, Comma formatting: ${shouldUseComma})`
    );

    // Test Number constructor
    const testParse = Number("1234,56");
    console.log(
      `🧪 Test: Number("1234,56") = ${testParse} (should be 1234.56)`
    );
  }

  // Method to format numbers for display (respects current locale)
  formatForDisplay(number, decimals = 4) {
    if (number === null || number === undefined || isNaN(number)) {
      return "XXX";
    }

    if (this.shouldUseCommaFormatting()) {
      // Use original toFixed to get dot format, then convert to comma
      const result = this.originalToFixed.call(Number(number), decimals);
      return result.replace(".", ",");
    } else {
      return Number(number).toFixed(decimals);
    }
  }

  // Utility method to replace toFixed with comma formatting
  toFixedDanish(number, decimals = 4) {
    if (number === null || number === undefined || isNaN(number)) {
      return "XXX";
    }

    if (this.shouldUseCommaFormatting()) {
      // Use original toFixed to get dot format, then convert to comma
      const result = this.originalToFixed.call(Number(number), decimals);
      return result.replace(".", ",");
    } else {
      return Number(number).toFixed(decimals);
    }
  }

  // Utility method to replace toLocaleString with comma formatting
  toLocaleStringDanish(number, options = {}) {
    if (number === null || number === undefined || isNaN(number)) {
      return "XXX";
    }

    if (this.shouldUseCommaFormatting()) {
      // Use appropriate locale for comma formatting
      const currentLanguage = this.getCurrentLanguage();
      const localeMap = {
        da: "da-DK",
        de: "de-DE",
        fr: "fr-FR",
        nl: "nl-NL",
      };
      const locale = localeMap[currentLanguage] || "da-DK";
      return this.originalToLocaleString.call(Number(number), locale, options);
    } else {
      return Number(number).toLocaleString(options);
    }
  }

  // Method to parse numbers from display (handles comma separator)
  parseFromDisplay(displayValue) {
    if (!displayValue || displayValue === "") {
      return "";
    }

    if (this.shouldUseCommaFormatting()) {
      return displayValue.replace(",", ".");
    }
    return displayValue;
  }

  // Method to handle input changes for number inputs
  handleNumberInputChange(value, onChange) {
    if (this.shouldUseCommaFormatting()) {
      // Allow comma as decimal separator in input
      if (value === "" || /^-?\d*[,.]?\d*$/.test(value)) {
        onChange(value);
      }
    } else {
      // Standard decimal input validation
      if (value === "" || /^-?\d*\.?\d*$/.test(value)) {
        onChange(value);
      }
    }
  }

  // Method to get display value for number inputs
  getDisplayValue(value) {
    if (value === null || value === undefined || value === "") {
      return "";
    }

    if (this.shouldUseCommaFormatting()) {
      // Convert dot to comma for display
      return String(value).replace(".", ",");
    }
    return String(value);
  }

  // Method to get calculation value from display value
  getCalculationValue(displayValue) {
    if (!displayValue || displayValue === "") {
      return "";
    }

    if (this.shouldUseCommaFormatting()) {
      // Convert comma to dot for calculations
      return displayValue.replace(",", ".");
    }
    return displayValue;
  }

  // Method to ensure payload values are always in dot format
  ensurePayloadFormat(value) {
    if (value === null || value === undefined || value === "") {
      return value;
    }

    // Convert to string and replace comma with dot
    const stringValue = String(value);
    return stringValue.replace(",", ".");
  }

  // Method to format numbers for API/payload (always dot format)
  formatForPayload(value) {
    if (value === null || value === undefined) {
      return value;
    }

    // Ensure it's a number first, then convert to string with dot
    const numValue = Number(this.ensurePayloadFormat(value));
    if (isNaN(numValue)) {
      return value; // Return original if not a valid number
    }

    return numValue.toString();
  }

  // Method to recursively format all numbers in an object for display
  formatObjectForDisplay(obj) {
    if (obj === null || obj === undefined) {
      return obj;
    }

    if (Array.isArray(obj)) {
      return obj.map((item) => this.formatObjectForDisplay(item));
    }

    if (typeof obj === "object") {
      const formatted = {};
      for (const [key, value] of Object.entries(obj)) {
        formatted[key] = this.formatObjectForDisplay(value);
      }
      return formatted;
    }

    if (typeof obj === "number") {
      if (this.shouldUseCommaFormatting()) {
        // Format number with comma for display
        return this.formatForDisplay(obj);
      }
    }

    if (typeof obj === "string" && /^\d+\.\d+$/.test(obj)) {
      // If it's a string that looks like a number with dot, convert to comma for display
      if (this.shouldUseCommaFormatting()) {
        return obj.replace(".", ",");
      }
    }

    return obj;
  }
}

// Create singleton instance
const danishNumberFormatter = new DanishNumberFormatter();

// Initialize immediately when module loads
danishNumberFormatter.initialize();

// Also try to initialize again after a short delay to catch any timing issues
setTimeout(() => {
  if (!danishNumberFormatter.isInitialized) {
    console.log("🔄 Re-initializing Danish number formatting...");
    danishNumberFormatter.initialize();
  }
}, 100);

export default danishNumberFormatter;
